import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface CurrentUser {
  id: string;
  identifier: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  nettingView: boolean;
  applications: any[];
}

@Injectable({
  providedIn: 'root'
})
export class CurrentUserService {

  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isLoadedSubject = new BehaviorSubject<boolean>(false);
  public isLoaded$ = this.isLoadedSubject.asObservable();

  constructor(private http: HttpClient) {
    // Restaurer l'utilisateur depuis sessionStorage au démarrage
    // Ainsi le guard peut accéder à l'utilisateur immédiatement sans attendre l'API
    const stored = sessionStorage.getItem('user');
    if (stored) {
      try {
        const raw = JSON.parse(stored);
        // Compatibilité : anciens objets utilisent "profile", nouveaux utilisent "role"
        const user: CurrentUser = { ...raw, role: raw.role ?? raw.profile };
        this.currentUserSubject.next(user);
        this.isLoadedSubject.next(true);
      } catch (_) {}
    }
  }

  /**
   * Charger l'utilisateur depuis le backend
   */
  loadCurrentUser(): Observable<CurrentUser | null> {
    return this.http.get<CurrentUser>('/api/auth/me').pipe(
      tap((user: CurrentUser) => {
        // Compatibilité : anciens backends peuvent retourner "profile" au lieu de "role"
        const normalized: CurrentUser = { ...user, role: user.role ?? (user as any).profile };
        this.currentUserSubject.next(normalized);
        this.isLoadedSubject.next(true);
        sessionStorage.setItem('user', JSON.stringify(normalized));
      }),
      catchError((err: any) => {
        // Fallback: essayer de récupérer depuis sessionStorage (mode sans auth)
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            this.currentUserSubject.next(user);
            this.isLoadedSubject.next(true);
            return of(user);
          } catch (parseErr) {
            console.error('[CurrentUserService] Erreur de parsing sessionStorage');
          }
        }
        this.isLoadedSubject.next(true);
        return of(null);
      })
    );
  }

  /**
   * Obtenir l'utilisateur actuel (synchrone)
   */
  getCurrentUser(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Observable pour s'abonner aux changements utilisateur
   */
  getCurrentUser$(): Observable<CurrentUser | null> {
    return this.currentUser$;
  }

  /**
   * Vérifier si l'utilisateur est chargé
   */
  isUserLoaded(): boolean {
    return this.isLoadedSubject.value;
  }

  /**
   * Définir l'utilisateur manuellement (après login par exemple)
   */
  setCurrentUser(user: CurrentUser | null): void {
    this.currentUserSubject.next(user);
    if (user) {
      sessionStorage.setItem('user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('user');
    }
  }

  /**
   * Déconnecter l'utilisateur
   */
  logout(): void {
    this.currentUserSubject.next(null);
    sessionStorage.removeItem('user');
  }

  /**
   * Vérifier si l'utilisateur a un rôle spécifique
   */
  hasRole(role: string): boolean {
    const user = this.currentUserSubject.value;
    return user?.role === role;
  }

  /**
   * Vérifier si l'utilisateur est admin
   */
  isAdmin(): boolean {
    return this.hasRole('Administrateur');
  }

  /**
   * Vérifier si l'utilisateur a accès à la vue netting
   */
  hasNettingView(): boolean {
    return this.currentUserSubject.value?.nettingView ?? false;
  }

  /**
   * Obtenir les applications de l'utilisateur
   */
  getUserApplications(): any[] {
    return this.currentUserSubject.value?.applications ?? [];
  }
}
